import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { Transportation, CreateTransportationRequest, UpdateTransportationRequest, DakoTestConnectionResult, DakoDiscoveredVehicle, TRANSPORTATION_TYPES } from '../../types/transportation';
import { transportationApi } from '../../services/transportationApi';

interface TransportationFormProps {
  transportation?: Transportation;
  onSave: (data: CreateTransportationRequest | UpdateTransportationRequest) => void;
  onCancel: () => void;
  loading: boolean;
}

const TransportationForm: React.FC<TransportationFormProps> = ({
  transportation,
  onSave,
  onCancel,
  loading
}) => {
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    description: '',
    website: '',
    contactEmail: '',
    contactPhone: '',
    address: '',
    logo: '',
    costPerKm: '',
    capacity: '',
    features: '',
    isActive: true
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // ── DAKO Telematik state ───────────────────────────────────────────────
  const [dakoOpen, setDakoOpen] = useState(!!transportation?.hasDakoConfig);
  const [dakoCompanyIdent, setDakoCompanyIdent] = useState(transportation?.dakoCompanyIdent ?? '');
  const [dakoVehicleIdent, setDakoVehicleIdent] = useState(transportation?.dakoVehicleIdent ?? '');
  const [dakoUsername, setDakoUsername] = useState(transportation?.dakoUsername ?? '');
  const [dakoPassword, setDakoPassword] = useState('');
  const [dakoPollIntervalSeconds, setDakoPollIntervalSeconds] = useState<string>(
    transportation?.dakoPollIntervalSeconds != null ? String(transportation.dakoPollIntervalSeconds) : '10',
  );
  const [dakoApiBaseUrl, setDakoApiBaseUrl] = useState(transportation?.dakoApiBaseUrl ?? '');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<DakoTestConnectionResult | null>(null);
  // ── DAKO discovery state ───────────────────────────────────────────────
  const [dakoAdvancedOpen, setDakoAdvancedOpen] = useState(false);
  const [dakoDiscovering, setDakoDiscovering] = useState(false);
  const [dakoDiscoverError, setDakoDiscoverError] = useState<string | null>(null);
  const [dakoCompanies, setDakoCompanies] = useState<string[]>([]);
  const [dakoVehicles, setDakoVehicles] = useState<DakoDiscoveredVehicle[]>([]);

  useEffect(() => {
    if (transportation) {
      setFormData({
        name: transportation.name || '',
        type: transportation.type || '',
        description: transportation.description || '',
        website: transportation.website || '',
        contactEmail: transportation.contactEmail || '',
        contactPhone: transportation.contactPhone || '',
        address: transportation.address || '',
        logo: transportation.logo || '',
        costPerKm: transportation.costPerKm?.toString() || '',
        capacity: transportation.capacity?.toString() || '',
        features: transportation.features || '',
        isActive: transportation.isActive
      });
      setDakoOpen(!!transportation.hasDakoConfig);
      setDakoCompanyIdent(transportation.dakoCompanyIdent ?? '');
      setDakoVehicleIdent(transportation.dakoVehicleIdent ?? '');
      setDakoUsername(transportation.dakoUsername ?? '');
      setDakoPassword('');
      setDakoPollIntervalSeconds(
        transportation.dakoPollIntervalSeconds != null ? String(transportation.dakoPollIntervalSeconds) : '10',
      );
      setDakoApiBaseUrl(transportation.dakoApiBaseUrl ?? '');
      setDakoCompanies([]);
      setDakoVehicles([]);
      setDakoDiscoverError(null);
    }
  }, [transportation]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name ist erforderlich';
    }

    if (!formData.type.trim()) {
      newErrors.type = 'Typ ist erforderlich';
    }

    if (formData.contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
      newErrors.contactEmail = 'Ungültige E-Mail-Adresse';
    }

    if (formData.website && !/^https?:\/\/.+/.test(formData.website)) {
      newErrors.website = 'Website muss mit http:// oder https:// beginnen';
    }

    if (formData.costPerKm && (isNaN(Number(formData.costPerKm)) || Number(formData.costPerKm) < 0)) {
      newErrors.costPerKm = 'Kosten pro km müssen eine positive Zahl sein';
    }

    if (formData.capacity && (isNaN(Number(formData.capacity)) || Number(formData.capacity) < 0)) {
      newErrors.capacity = 'Kapazität muss eine positive Zahl sein';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const submitData: CreateTransportationRequest | UpdateTransportationRequest = {
      name: formData.name.trim(),
      type: formData.type.trim(),
      description: formData.description.trim() || undefined,
      website: formData.website.trim() || undefined,
      contactEmail: formData.contactEmail.trim() || undefined,
      contactPhone: formData.contactPhone.trim() || undefined,
      address: formData.address.trim() || undefined,
      logo: formData.logo.trim() || undefined,
      costPerKm: formData.costPerKm ? formData.costPerKm.toString() : undefined,
      capacity: formData.capacity ? Number(formData.capacity) : undefined,
      features: formData.features.trim() || undefined,
      isActive: formData.isActive,
      // ── DAKO Telematik ──────────────────────────────────────────────────
      dakoCompanyIdent: dakoCompanyIdent.trim() || null,
      dakoVehicleIdent: dakoVehicleIdent.trim() || null,
      dakoUsername: dakoUsername.trim() || null,
      // Password: only send when a value was entered (never clear on empty).
      ...(dakoPassword ? { dakoPassword } : {}),
      dakoPollIntervalSeconds: dakoPollIntervalSeconds ? Number(dakoPollIntervalSeconds) : null,
      dakoApiBaseUrl: dakoApiBaseUrl.trim() || null,
    };

    onSave(submitData);
  };

  const handleTest = async () => {
    if (!transportation) return;
    setTesting(true);
    setTestResult(null);
    try {
      const result = await transportationApi.testDakoConnection(transportation.id);
      setTestResult(result);
    } catch (err: any) {
      setTestResult({ success: false, message: err?.message || 'Verbindung fehlgeschlagen', vehicleIdent: '' });
    } finally {
      setTesting(false);
    }
  };

  const runDiscover = async (companyOverride?: string) => {
    if (!dakoUsername.trim() || !dakoPassword.trim()) {
      setDakoDiscoverError('Bitte Benutzername und Passwort eingeben.');
      return;
    }
    setDakoDiscovering(true);
    setDakoDiscoverError(null);
    try {
      const company = (companyOverride ?? dakoCompanyIdent).trim();
      const result = await transportationApi.discoverDakoVehicles({
        username: dakoUsername.trim(),
        password: dakoPassword,
        companyIdent: company || undefined,
        apiBaseUrl: dakoApiBaseUrl.trim() || undefined,
      });

      if (result.error) {
        setDakoDiscoverError(result.error);
        setDakoCompanies(result.companies ?? []);
        setDakoVehicles([]);
        return;
      }

      // Multiple companies and none resolved yet → let the user pick one.
      if ((result.companies?.length ?? 0) > 1 && !result.selectedCompany) {
        setDakoCompanies(result.companies);
        setDakoVehicles([]);
        return;
      }

      setDakoCompanies(result.companies ?? []);
      if (result.selectedCompany) {
        setDakoCompanyIdent(result.selectedCompany);
      }
      setDakoVehicles(result.vehicles ?? []);
      if ((result.vehicles?.length ?? 0) === 0) {
        setDakoDiscoverError('Für diese Firma wurden keine Fahrzeuge gefunden.');
      }
    } catch (err: any) {
      setDakoDiscoverError(err?.message || 'Fahrzeuge konnten nicht geladen werden.');
    } finally {
      setDakoDiscovering(false);
    }
  };

  const handleCompanySelect = (company: string) => {
    setDakoCompanyIdent(company);
    setDakoVehicles([]);
    if (company) {
      void runDiscover(company);
    }
  };

  const vehicleLabel = (v: DakoDiscoveredVehicle): string => {
    const kennzeichen = v.licenceNumber?.trim();
    const alias = v.alias?.trim();
    if (kennzeichen && alias) return `${kennzeichen} (${alias})`;
    if (kennzeichen) return kennzeichen;
    if (alias) return alias;
    return v.ident;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={headerStyle}>
          <h2 style={titleStyle}>
            {transportation ? 'Verkehrsmittel bearbeiten' : 'Neues Verkehrsmittel'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} style={formStyle}>
          <div style={formGroupStyle}>
            <div style={rowStyle}>
              <div style={{ flex: 1, marginRight: '1rem' }}>
                <label style={labelStyle}>Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  style={errors.name ? errorInputStyle : inputStyle}
                  placeholder="z.B. Deutsche Bahn, Lufthansa"
                  disabled={loading}
                />
                {errors.name && <div style={errorTextStyle}>{errors.name}</div>}
              </div>

              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Typ *</label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  style={errors.type ? errorInputStyle : inputStyle}
                  disabled={loading}
                >
                  <option value="">Typ auswählen</option>
                  {TRANSPORTATION_TYPES.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.icon} {type.label}
                    </option>
                  ))}
                </select>
                {errors.type && <div style={errorTextStyle}>{errors.type}</div>}
              </div>
            </div>
          </div>

          <div style={formGroupStyle}>
            <label style={labelStyle}>Beschreibung</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              style={textareaStyle}
              placeholder="Beschreibung des Verkehrsmittels"
              rows={3}
              disabled={loading}
            />
          </div>

          <div style={formGroupStyle}>
            <div style={rowStyle}>
              <div style={{ flex: 1, marginRight: '1rem' }}>
                <label style={labelStyle}>Website</label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  style={errors.website ? errorInputStyle : inputStyle}
                  placeholder="https://www.example.com"
                  disabled={loading}
                />
                {errors.website && <div style={errorTextStyle}>{errors.website}</div>}
              </div>

              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Logo URL</label>
                <input
                  type="url"
                  name="logo"
                  value={formData.logo}
                  onChange={handleInputChange}
                  style={inputStyle}
                  placeholder="https://www.example.com/logo.png"
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          <div style={formGroupStyle}>
            <div style={rowStyle}>
              <div style={{ flex: 1, marginRight: '1rem' }}>
                <label style={labelStyle}>E-Mail</label>
                <input
                  type="email"
                  name="contactEmail"
                  value={formData.contactEmail}
                  onChange={handleInputChange}
                  style={errors.contactEmail ? errorInputStyle : inputStyle}
                  placeholder="kontakt@example.com"
                  disabled={loading}
                />
                {errors.contactEmail && <div style={errorTextStyle}>{errors.contactEmail}</div>}
              </div>

              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Telefon</label>
                <input
                  type="tel"
                  name="contactPhone"
                  value={formData.contactPhone}
                  onChange={handleInputChange}
                  style={inputStyle}
                  placeholder="+49 123 456789"
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          <div style={formGroupStyle}>
            <label style={labelStyle}>Adresse</label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              style={inputStyle}
              placeholder="Straße, PLZ Ort"
              disabled={loading}
            />
          </div>

          <div style={formGroupStyle}>
            <div style={rowStyle}>
              <div style={{ flex: 1, marginRight: '1rem' }}>
                <label style={labelStyle}>Kosten pro km (€)</label>
                <input
                  type="number"
                  name="costPerKm"
                  value={formData.costPerKm}
                  onChange={handleInputChange}
                  style={errors.costPerKm ? errorInputStyle : inputStyle}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  disabled={loading}
                />
                {errors.costPerKm && <div style={errorTextStyle}>{errors.costPerKm}</div>}
              </div>

              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Kapazität</label>
                <input
                  type="number"
                  name="capacity"
                  value={formData.capacity}
                  onChange={handleInputChange}
                  style={errors.capacity ? errorInputStyle : inputStyle}
                  placeholder="Anzahl Personen/Gewicht"
                  min="0"
                  disabled={loading}
                />
                {errors.capacity && <div style={errorTextStyle}>{errors.capacity}</div>}
              </div>
            </div>
          </div>

          <div style={formGroupStyle}>
            <label style={labelStyle}>Ausstattung/Features</label>
            <textarea
              name="features"
              value={formData.features}
              onChange={handleInputChange}
              style={textareaStyle}
              placeholder="z.B. WLAN, Klimaanlage, Bordservice"
              rows={2}
              disabled={loading}
            />
          </div>

          {/* ── DAKO Telematik (einklappbar) ─────────────────────────────── */}
          <div style={dakoSectionStyle}>
            <button type="button" style={dakoHeaderStyle} onClick={() => setDakoOpen(o => !o)}>
              {dakoOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              DAKO Telematik {transportation?.hasDakoConfig && <span style={{ color: '#198754', fontSize: '0.8rem' }}>(konfiguriert)</span>}
            </button>

            {dakoOpen && (
              <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                  Benutzername und Passwort der DAKO Tachoweb Positions-API eingeben und anschließend
                  „Fahrzeuge laden“ klicken. Firma und Fahrzeug werden automatisch ermittelt.
                </div>

                {/* Row 1: Zugangsdaten */}
                <div style={rowStyle}>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Benutzername *</label>
                    <input style={inputStyle} value={dakoUsername} onChange={(e) => setDakoUsername(e.target.value)} autoComplete="off" disabled={loading} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Passwort *</label>
                    <input
                      style={inputStyle}
                      type="password"
                      value={dakoPassword}
                      onChange={(e) => setDakoPassword(e.target.value)}
                      placeholder={transportation?.hasDakoConfig ? 'Unverändert lassen' : ''}
                      autoComplete="new-password"
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Row 2: Firma (optional) + Fahrzeuge laden */}
                <div style={{ ...rowStyle, alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Firmen-Kennung (optional)</label>
                    <input
                      style={inputStyle}
                      value={dakoCompanyIdent}
                      onChange={(e) => setDakoCompanyIdent(e.target.value)}
                      placeholder="Wird automatisch ermittelt"
                      disabled={loading || dakoDiscovering}
                    />
                  </div>
                  <div>
                    <button
                      type="button"
                      style={testButtonStyle}
                      onClick={() => runDiscover()}
                      disabled={loading || dakoDiscovering || !dakoUsername.trim() || !dakoPassword.trim()}
                    >
                      {dakoDiscovering ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite', verticalAlign: 'middle', marginRight: 4 }} /> : null}
                      Fahrzeuge laden
                    </button>
                  </div>
                </div>

                {dakoDiscoverError && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.85rem', color: '#dc3545' }}>
                    <XCircle size={16} /> {dakoDiscoverError}
                  </span>
                )}

                {/* Row 3a: Firmenauswahl (bei mehreren Firmen) */}
                {dakoCompanies.length > 1 && (
                  <div style={rowStyle}>
                    <div style={{ flex: 1 }}>
                      <label style={labelStyle}>Firma auswählen</label>
                      <select
                        style={inputStyle}
                        value={dakoCompanyIdent}
                        onChange={(e) => handleCompanySelect(e.target.value)}
                        disabled={loading || dakoDiscovering}
                      >
                        <option value="">— Firma wählen —</option>
                        {dakoCompanies.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Row 3b: Fahrzeugauswahl */}
                {dakoVehicles.length > 0 && (
                  <div style={rowStyle}>
                    <div style={{ flex: 1 }}>
                      <label style={labelStyle}>Fahrzeug (Kennzeichen)</label>
                      <select
                        style={inputStyle}
                        value={dakoVehicleIdent}
                        onChange={(e) => setDakoVehicleIdent(e.target.value)}
                        disabled={loading}
                      >
                        <option value="">— Fahrzeug wählen —</option>
                        {dakoVehicles.map((v) => (
                          <option key={v.ident} value={v.ident}>
                            {vehicleLabel(v)}{v.hasGpsTransmitter ? '' : ' — kein GPS'}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Bereits gespeicherte Fahrzeug-Kennung anzeigen, wenn noch keine Liste geladen */}
                {dakoVehicles.length === 0 && dakoVehicleIdent && (
                  <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                    Aktuell hinterlegte Fahrzeug-Kennung: <strong>{dakoVehicleIdent}</strong>
                  </div>
                )}

                {/* Row 4: Erweitert (einklappbar) */}
                <div>
                  <button
                    type="button"
                    onClick={() => setDakoAdvancedOpen(o => !o)}
                    style={{ ...dakoHeaderStyle, background: 'transparent', padding: '0.25rem 0', fontWeight: 500, width: 'auto' }}
                  >
                    {dakoAdvancedOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    Erweitert
                  </button>
                  {dakoAdvancedOpen && (
                    <div style={{ ...rowStyle, marginTop: '0.5rem' }}>
                      <div style={{ width: 160 }}>
                        <label style={labelStyle}>Poll-Intervall (Sek.)</label>
                        <input style={inputStyle} type="number" min={2} value={dakoPollIntervalSeconds} onChange={(e) => setDakoPollIntervalSeconds(e.target.value)} disabled={loading} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={labelStyle}>API-Basis-URL (optional)</label>
                        <input style={inputStyle} value={dakoApiBaseUrl} onChange={(e) => setDakoApiBaseUrl(e.target.value)} placeholder="https://api.tachoweb.eu/Positions.rest/v1" disabled={loading} />
                      </div>
                    </div>
                  )}
                </div>

                {transportation && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <button type="button" style={testButtonStyle} onClick={handleTest} disabled={testing || !transportation?.hasDakoConfig}>
                      {testing ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite', verticalAlign: 'middle' }} /> : null} Verbindung testen
                    </button>
                    {!transportation?.hasDakoConfig && (
                      <span style={{ fontSize: '0.8rem', color: '#adb5bd' }}>Erst nach dem Speichern testbar.</span>
                    )}
                    {testResult && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.85rem', color: testResult.success ? '#198754' : '#dc3545' }}>
                        {testResult.success ? <CheckCircle2 size={16} /> : <XCircle size={16} />} {testResult.message}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={checkboxGroupStyle}>
            <label style={checkboxLabelStyle}>
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleInputChange}
                style={checkboxStyle}
                disabled={loading}
              />
              Aktiv
            </label>
          </div>

          <div style={buttonGroupStyle}>
            <button
              type="button"
              onClick={onCancel}
              style={cancelButtonStyle}
              disabled={loading}
            >
              Abbrechen
            </button>
            <button
              type="submit"
              style={submitButtonStyle}
              disabled={loading}
            >
              {loading ? 'Speichern...' : (transportation ? 'Aktualisieren' : 'Erstellen')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TransportationForm;

// CSS Styles
const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '12px',
  padding: '2rem',
  width: '90%',
  maxWidth: '600px',
  maxHeight: '90vh',
  overflow: 'auto',
  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
};

const headerStyle: React.CSSProperties = {
  marginBottom: '1.5rem',
  borderBottom: '1px solid #e5e7eb',
  paddingBottom: '1rem',
};

const titleStyle: React.CSSProperties = {
  fontSize: '1.5rem',
  fontWeight: 'bold',
  color: '#1f2937',
  margin: 0,
};

const formStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
};

const formGroupStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  gap: '1rem',
  alignItems: 'flex-start',
};

const labelStyle: React.CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: '500',
  color: '#374151',
  marginBottom: '0.25rem',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem 0.75rem',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  fontSize: '0.875rem',
  transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
  outline: 'none',
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  resize: 'vertical',
  minHeight: '80px',
};

const errorInputStyle: React.CSSProperties = {
  ...inputStyle,
  borderColor: '#ef4444',
  boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.1)',
};

const errorTextStyle: React.CSSProperties = {
  color: '#ef4444',
  fontSize: '0.75rem',
  marginTop: '0.25rem',
};

const checkboxGroupStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  marginTop: '0.5rem',
};

const checkboxStyle: React.CSSProperties = {
  marginRight: '0.5rem',
};

const checkboxLabelStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  fontSize: '0.875rem',
  color: '#374151',
  cursor: 'pointer',
};

const buttonGroupStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '0.75rem',
  marginTop: '1.5rem',
  paddingTop: '1rem',
  borderTop: '1px solid #e5e7eb',
};

const cancelButtonStyle: React.CSSProperties = {
  padding: '0.5rem 1rem',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  backgroundColor: 'white',
  color: '#374151',
  fontSize: '0.875rem',
  fontWeight: '500',
  cursor: 'pointer',
  transition: 'all 0.15s ease-in-out',
};

const submitButtonStyle: React.CSSProperties = {
  padding: '0.5rem 1rem',
  border: 'none',
  borderRadius: '6px',
  backgroundColor: '#3b82f6',
  color: 'white',
  fontSize: '0.875rem',
  fontWeight: '500',
  cursor: 'pointer',
  transition: 'all 0.15s ease-in-out',
};

const dakoSectionStyle: React.CSSProperties = {
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  marginTop: '0.5rem',
  overflow: 'hidden',
};

const dakoHeaderStyle: React.CSSProperties = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.75rem 1rem',
  background: '#f9fafb',
  border: 'none',
  cursor: 'pointer',
  fontSize: '0.9rem',
  fontWeight: 600,
  color: '#1f2937',
  textAlign: 'left',
};

const testButtonStyle: React.CSSProperties = {
  padding: '0.5rem 1rem',
  borderRadius: '6px',
  fontSize: '0.875rem',
  fontWeight: 500,
  cursor: 'pointer',
  backgroundColor: '#f9fafb',
  color: '#374151',
  border: '1px solid #d1d5db',
};
